import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Palette, ImageIcon, Globe, Save, Lock, Loader2, ExternalLink, ArrowRight, Sparkles, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getMyBrand, updateMyBrand, getBrandStats, uploadBrandLogo } from '../lib/api';

export default function AccountBrand() {
  const nav = useNavigate();
  const { user, brand: authBrand, setBrand, loading: authLoading } = useAuth();
  const [brand, setLocalBrand] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav('/login'); return; }
    (async () => {
      try { const b = await getMyBrand(); setLocalBrand(b); } catch { toast.error('Errore caricamento'); }
      try { const s = await getBrandStats(); setStats(s); } catch {}
    })();
  }, [user, authLoading, nav]);

  if (!brand) return (
    <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0d63b0]" /></div>
  );

  const isFree = brand.plan === 'free';
  const canDomain = brand.plan === 'pro';

  const change = (k, v) => setLocalBrand((b) => ({ ...b, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: brand.name,
        logo_url: brand.logo_url,
        primary_color: brand.primary_color,
        accent_color: brand.accent_color,
        hero_title: brand.hero_title,
        hero_subtitle: brand.hero_subtitle,
        company_url: brand.company_url,
      };
      const updated = await updateMyBrand(body);
      setLocalBrand(updated);
      setBrand(updated);
      toast.success('Personalizzazione salvata');
    } catch (e) { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const brandUrl = `${window.location.origin}/b/${brand.slug}`;

  return (
    <div className="py-10">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0d63b0]">Pannello Reseller</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-slate-900">Personalizza il tuo brand</h1>
            <p className="mt-1 text-slate-600 text-sm">Logo, colori, testi e dominio del tuo servizio Secure Push.</p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline"><Link to={`/b/${brand.slug}`}><ExternalLink className="h-4 w-4 mr-1.5" /> Anteprima landing</Link></Button>
            <Button asChild className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white"><Link to="/account/subscription">Piano: <span className="ml-1.5 font-bold capitalize">{brand.plan}</span></Link></Button>
          </div>
        </div>

        {/* Brand URL banner */}
        <Card className="border border-slate-200 p-5 mb-6 bg-slate-50/60">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-700"><Globe className="h-4 w-4 text-[#0d63b0]" /> Il tuo URL brand:</div>
            <code className="font-mono text-sm text-[#0d63b0] bg-white border border-slate-200 rounded px-2 py-1">{brandUrl}</code>
            {brand.custom_domain && brand.domain_verified && (
              <>
                <span className="text-slate-400 text-sm">oppure</span>
                <code className="font-mono text-sm text-emerald-700 bg-white border border-emerald-200 rounded px-2 py-1">https://{brand.custom_domain}</code>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold">Verificato</span>
              </>
            )}
            {brand.custom_domain && !brand.domain_verified && (
              <>
                <span className="text-slate-400 text-sm">·</span>
                <code className="font-mono text-sm text-slate-500 bg-white border border-slate-200 rounded px-2 py-1 line-through">https://{brand.custom_domain}</code>
                <Link to="/account/domain" className="text-[11px] font-semibold text-amber-700 hover:underline">Completa verifica DNS →</Link>
              </>
            )}
            <div className="flex-1" />
            {stats && <div className="text-xs text-slate-500">Push totali: <strong className="text-slate-800">{stats.total_pushes}</strong> · attivi: <strong className="text-slate-800">{stats.active_pushes}</strong></div>}
          </div>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Editor */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border border-slate-200 p-6">
              <div className="font-semibold text-slate-900 flex items-center gap-2"><Palette className="h-4 w-4 text-[#0d63b0]" /> Identità brand</div>
              <div className="mt-5 grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Nome brand</Label>
                  <Input value={brand.name} onChange={(e) => change('name', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input value={brand.slug} disabled className="mt-1 font-mono text-slate-500" />
                </div>
              </div>
              <div className="mt-4">
                <Label className="flex items-center gap-2">URL sito aziendale {isFree && <span className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">Free</span>}</Label>
                <Input value={brand.company_url || ''} onChange={(e) => change('company_url', e.target.value)} placeholder="https://azienda.it" className="mt-1" />
              </div>
            </Card>

            <Card className={`border border-slate-200 p-6 relative ${isFree ? 'opacity-95' : ''}`}>
              {isFree && <FreeBanner />}
              <div className="font-semibold text-slate-900 flex items-center gap-2"><ImageIcon className="h-4 w-4 text-[#0d63b0]" /> Logo e colori</div>
              <div className="mt-5 space-y-4">
                <div>
                  <Label>Logo del brand</Label>
                  <div className="mt-2 grid md:grid-cols-2 gap-3">
                    <div>
                      <label className={`flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg p-4 cursor-pointer hover:border-[#0d63b0] transition-colors ${isFree ? 'pointer-events-none opacity-60' : ''}`}>
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" className="hidden" disabled={isFree || uploadingLogo}
                          onChange={async (e) => {
                            const f = e.target.files?.[0]; if (!f) return;
                            if (f.size > 2 * 1024 * 1024) { toast.error('File troppo grande (max 2MB)'); return; }
                            setUploadingLogo(true);
                            try {
                              const updated = await uploadBrandLogo(f);
                              setLocalBrand(updated); setBrand(updated);
                              toast.success('Logo caricato');
                            } catch (err) { toast.error('Errore upload'); }
                            finally { setUploadingLogo(false); }
                          }} />
                        {uploadingLogo ? <Loader2 className="h-6 w-6 animate-spin text-[#0d63b0]" /> : <Upload className="h-6 w-6 text-[#0d63b0]" />}
                        <div className="mt-2 text-xs font-semibold text-slate-800">Carica file</div>
                        <div className="text-[10px] text-slate-500">PNG, JPG, WEBP, SVG — max 2MB</div>
                      </label>
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">O da URL</div>
                      <Input value={brand.logo_url && brand.logo_url.startsWith('data:') ? '' : (brand.logo_url || '')} onChange={(e) => change('logo_url', e.target.value)} placeholder="https://cdn.tuobrand.it/logo.png" className="font-mono text-xs" disabled={isFree} />
                      <div className="mt-1 text-[10px] text-slate-500">Se hai caricato un file, l'URL viene ignorato.</div>
                    </div>
                  </div>
                  {brand.logo_url && (
                    <div className="mt-3 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-2">
                      <img src={brand.logo_url} alt="Logo attuale" className="h-10 w-auto max-w-[140px] object-contain bg-white rounded border border-slate-200 p-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      <div className="text-xs text-slate-600">Logo attualmente in uso</div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Colore primario</Label>
                    <div className="mt-1 flex gap-2">
                      <input type="color" value={brand.primary_color || '#0d63b0'} onChange={(e) => change('primary_color', e.target.value)} className="h-10 w-14 rounded border border-slate-200 cursor-pointer" disabled={isFree} />
                      <Input value={brand.primary_color || '#0d63b0'} onChange={(e) => change('primary_color', e.target.value)} className="font-mono" disabled={isFree} />
                    </div>
                  </div>
                  <div>
                    <Label>Colore accento</Label>
                    <div className="mt-1 flex gap-2">
                      <input type="color" value={brand.accent_color || '#1e88d6'} onChange={(e) => change('accent_color', e.target.value)} className="h-10 w-14 rounded border border-slate-200 cursor-pointer" disabled={isFree} />
                      <Input value={brand.accent_color || '#1e88d6'} onChange={(e) => change('accent_color', e.target.value)} className="font-mono" disabled={isFree} />
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className={`border border-slate-200 p-6 relative ${isFree ? 'opacity-95' : ''}`}>
              {isFree && <FreeBanner />}
              <div className="font-semibold text-slate-900">Testi landing brand</div>
              <div className="mt-5 space-y-4">
                <div>
                  <Label>Titolo hero</Label>
                  <Input value={brand.hero_title || ''} onChange={(e) => change('hero_title', e.target.value)} placeholder="Condividi password in modo sicuro" className="mt-1" disabled={isFree} />
                </div>
                <div>
                  <Label>Sottotitolo hero</Label>
                  <Textarea rows={3} value={brand.hero_subtitle || ''} onChange={(e) => change('hero_subtitle', e.target.value)} placeholder="Con Acme Secure Push generi link cifrati che si auto-distruggono..." className="mt-1" disabled={isFree} />
                </div>
              </div>
            </Card>

            <Card className={`border border-slate-200 p-6 relative ${!canDomain ? 'opacity-95' : ''}`}>
              {!canDomain && <ProBanner />}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-semibold text-slate-900 flex items-center gap-2"><Globe className="h-4 w-4 text-[#0d63b0]" /> Dominio personalizzato</div>
                  <p className="mt-1 text-xs text-slate-500">Solo piano <strong>Pro</strong>. Configura CNAME + TXT del tuo dominio e servi i push dal tuo indirizzo.</p>
                </div>
                <Button asChild variant="outline" disabled={!canDomain}><Link to="/account/domain">Apri configurazione DNS <ArrowRight className="h-4 w-4 ml-1.5" /></Link></Button>
              </div>
              {canDomain && brand.custom_domain && (
                <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3 flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-slate-600">Dominio:</span>
                  <code className="font-mono bg-white border border-slate-200 rounded px-2 py-0.5 text-slate-800">{brand.custom_domain}</code>
                  {brand.domain_verified ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold">Verificato</span>
                  ) : brand.domain_status === 'failed' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[11px] font-semibold">Verifica fallita</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold">In attesa verifica</span>
                  )}
                </div>
              )}
              {canDomain && !brand.custom_domain && (
                <div className="mt-4 text-xs text-slate-500">Nessun dominio configurato. Apri la configurazione DNS per aggiungerne uno.</div>
              )}
            </Card>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white h-11 px-6">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Salva modifiche
              </Button>
            </div>
          </div>

          {/* Live preview */}
          <div>
            <div className="sticky top-20">
              <Card className="border border-slate-200 p-4 overflow-hidden">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Anteprima live</div>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2" style={{ backgroundColor: brand.primary_color + '10' }}>
                    {brand.logo_url ? (
                      <img src={brand.logo_url} alt="logo" className="h-7 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      <div className="h-7 w-7 rounded flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: brand.primary_color }}>{(brand.name || 'B')[0]}</div>
                    )}
                    <div className="font-bold text-slate-900 text-sm">{brand.name || 'Il tuo brand'}</div>
                    <div className="flex-1" />
                    <div className="text-[10px] text-slate-500 font-mono truncate">{brand.custom_domain && brand.domain_verified ? brand.custom_domain : `/b/${brand.slug}`}</div>
                  </div>
                  <div className="p-5" style={{ background: `linear-gradient(180deg, ${brand.accent_color}18, #fff)` }}>
                    <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: brand.primary_color }}>{brand.name} · Secure Push</div>
                    <h3 className="mt-2 text-xl font-extrabold text-slate-900 leading-tight">{brand.hero_title || 'Condividi password in modo sicuro'}</h3>
                    <p className="mt-2 text-xs text-slate-600 leading-relaxed">{brand.hero_subtitle || 'Link cifrati che si auto-distruggono dopo la visualizzazione o la scadenza impostata.'}</p>
                    <button className="mt-3 rounded-md px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm" style={{ backgroundColor: brand.primary_color }}>Crea Push</button>
                  </div>
                </div>
                <div className="mt-3 text-[11px] text-slate-500 text-center">Anteprima non salvata finché non premi “Salva”.</div>
              </Card>

              <Card className="mt-4 border-0 bg-gradient-to-br from-[#0d63b0] to-[#1e88d6] text-white p-4">
                <div className="text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Consiglio</div>
                <div className="mt-1 text-sm leading-snug">Sblocca colori e logo con <Link to="/account/subscription" className="font-bold underline">Starter</Link> a €19/mese.</div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FreeBanner() {
  return (
    <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-lg">
      <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 shadow flex items-center gap-3">
        <Lock className="h-4 w-4 text-[#0d63b0]" />
        <div className="text-sm text-slate-800">Sblocca con piano <strong>Starter</strong></div>
        <Link to="/account/subscription" className="text-sm font-semibold text-[#0d63b0] hover:underline inline-flex items-center gap-1">Passa a Starter <ArrowRight className="h-3.5 w-3.5" /></Link>
      </div>
    </div>
  );
}
function ProBanner() {
  return (
    <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-lg">
      <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 shadow flex items-center gap-3">
        <Lock className="h-4 w-4 text-[#0d63b0]" />
        <div className="text-sm text-slate-800">Disponibile con piano <strong>Pro</strong></div>
        <Link to="/account/subscription" className="text-sm font-semibold text-[#0d63b0] hover:underline inline-flex items-center gap-1">Passa a Pro <ArrowRight className="h-3.5 w-3.5" /></Link>
      </div>
    </div>
  );
}
